import React, { useEffect, useMemo } from 'react';
import { useField, useForm, useFormState } from 'react-final-form';
import { Box, Button, Card, Collapse, Typography } from '@mui/material';
import { Row, Spacer, ToggleInput } from '../../components';
import FormDateTextField from '../../components/FormDateTextField';
import { PricebookBundle } from '../../models/PricebookData';
import { getIntervalLabel } from '../../helpers/pricebookUtils';
import { RevShareResult } from '../../models/RevShareData';
import { AdminTierTypeEnum } from '../../services/drillDown';
import {
  RevShareFormValues,
  RevShareSettingOption,
  RevShareMethod,
  RevShareFields,
  calculateRevShareAmount,
  getEntriesForPricebook,
} from '../../data/rev-share/helpers/revShareSettingsUtils';
import { hasSubsequentEntry } from '../../data/rev-share/helpers/revShareEntryUtils';
import { formatDecimalOrWholeDollars } from '../../helpers/utilityFunctions';
import FormFieldRow, { toggleButtonStyle } from './components/FormFieldRow';
import RevShareMethodFields from './components/RevShareMethodFields';
import { useRevShareSettingSwitch } from './hooks/useRevShareSettingSwitch';

const revShareSettingOptions = ['Set fee share', 'No fee share'];
const revShareSettingLabel = 'Would you like to set fee share for this pricebook?';

interface RevShareEntryFormProps {
  handleSubmit: () => void;
  pricebook: PricebookBundle;
  revShareSettings: RevShareResult | undefined;
  activePrice: ReturnType<PricebookBundle['prices']['find']>;
  adminTier: AdminTierTypeEnum;
  isViewOnly?: boolean;
  onCancel: () => void;
}

const RevShareEntryForm: React.FC<RevShareEntryFormProps> = ({
  handleSubmit,
  pricebook,
  revShareSettings,
  activePrice,
  adminTier,
  isViewOnly = false,
  onCancel,
}) => {
  const form = useForm<RevShareFormValues>();
  const { hasSubmitErrors, pristine, values } = useFormState<RevShareFormValues>();

  const { input: parentTierInput } = useField<AdminTierTypeEnum | undefined>('parentTier');
  const { input: revShareSettingInput } = useField<RevShareSettingOption>('revShareSetting');
  const { input: revShareMethodInput } = useField<RevShareMethod | undefined>('revShareMethod');
  const { input: baseAmountInput } = useField<number | undefined>('baseAmount');
  const { input: revSharePctInput } = useField<number | undefined>('revSharePct');
  const { input: fixedAmountInput } = useField<number | undefined>('fixedAmount');
  const { input: setEndDateInput } = useField<string | undefined>('setEndDate');

  const revShareSetting = revShareSettingInput.value;
  const revShareMethod = revShareMethodInput.value;
  const setEndDate = setEndDateInput.value === 'Yes';

  useRevShareSettingSwitch({ form, revShareSetting });

  const existingEntries = useMemo(
    () =>
      getEntriesForPricebook(revShareSettings, pricebook.pricebook.id).filter(
        entry => !entry.tier || entry.tier.toLowerCase() === adminTier.toLowerCase(),
      ),
    [revShareSettings, pricebook.pricebook.id, adminTier],
  );

  const endDateRequired = useMemo(() => {
    const effective = values?.effectiveTimestamp;
    if (!effective) return false;
    return hasSubsequentEntry(existingEntries, new Date(effective), values?.existingEntryId);
  }, [existingEntries, values?.effectiveTimestamp, values?.existingEntryId]);

  useEffect(() => {
    if (endDateRequired && setEndDateInput.value !== 'Yes') {
      setEndDateInput.onChange('Yes');
    }
  }, [endDateRequired, setEndDateInput]);

  const inheritedRevShare: RevShareFields | undefined = useMemo(() => {
    if (!parentTierInput.value) return undefined;
    return form.getState().initialValues?.parentRevShareFields;
  }, [parentTierInput.value, form]);

  const calculatedRevShare = useMemo(() => {
    if (!revShareSetting) return null;
    if (revShareSetting === 'No fee share') return 0;
    if (!revShareMethod) return null;
    return calculateRevShareAmount(
      revShareMethod,
      baseAmountInput.value ?? 0,
      revSharePctInput.value ?? 0,
      fixedAmountInput.value ?? 0,
    );
  }, [
    revShareSetting,
    revShareMethod,
    baseAmountInput.value,
    revSharePctInput.value,
    fixedAmountInput.value,
  ]);

  const parentTierName = parentTierInput.value?.toLowerCase() || '';
  const showRevShareMethod = isViewOnly
    ? Boolean(revShareMethod)
    : Boolean(revShareSetting) && revShareSetting !== 'No fee share';
  const showTimingSection = isViewOnly || Boolean(revShareSetting);

  const disableSave = pristine || !revShareSetting || hasSubmitErrors;

  const intervalSuffix = getIntervalLabel(activePrice);

  const priceDisplay =
    activePrice?.price && activePrice.price > 0
      ? `$${formatDecimalOrWholeDollars(activePrice.price / 100)}${intervalSuffix}`
      : null;

  const pricebookDescription =
    pricebook.pricebook.internalAdminDescription ?? 'No description available';

  return (
    <Box display='flex' mt='5px' flexDirection='column' minHeight='calc(100vh - 120px)'>
      <Box
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        alignItems='flex-start'
        mb={4}>
        <Box display='flex' flex={1} flexDirection='column' mr={5}>
          <Typography variant='p16SemiBold' color='secondary.dark'>
            {pricebook.pricebook.pricebookName}
            {priceDisplay && (
              <Typography component='span' variant='p16' color='secondary.dark'>
                {` (${priceDisplay})`}
              </Typography>
            )}
          </Typography>
          <Typography variant='p14' color='secondary.main' sx={{ mt: '4px' }}>
            {pricebookDescription}
          </Typography>
        </Box>

        <Card
          variant='innerCard'
          sx={{
            flexShrink: 0,
            px: 4.5,
            py: 2,
            textAlign: 'center',
          }}>
          <Row style={{ justifyContent: 'center' }}>
            <Typography component='span' variant='p30SemiBold' color='info.main'>
              $
            </Typography>
            <Typography component='span' variant='p30SemiBold' color='secondary.dark'>
              {formatDecimalOrWholeDollars(calculatedRevShare ?? 0)}
            </Typography>
          </Row>
          <Typography variant='p12SemiBold' color='secondary.main'>
            fee share per sale
          </Typography>
        </Card>
      </Box>

      <Box mx={6} display='flex' flexDirection='column'>
        <FormFieldRow
          label='Fee share details'
          labelVariant='p18SemiBold'
          labelColor='secondary.dark'
        />
        <Spacer height='s15' />

        <Box display='flex' flexDirection='column' gap='25px'>
          {!isViewOnly && (
            <FormFieldRow label={revShareSettingLabel}>
              <Row style={{ minWidth: 0 }}>
                <ToggleInput<RevShareFormValues>
                  inputStyle={{ flexGrow: 1 }}
                  buttonStyle={toggleButtonStyle}
                  fieldName='revShareSetting'
                  items={revShareSettingOptions}
                  exclusive
                  required={false}
                />
              </Row>
            </FormFieldRow>
          )}

          <Collapse in={showRevShareMethod} timeout={300} unmountOnExit>
            <RevShareMethodFields
              revShareMethod={revShareMethod}
              readOnly={isViewOnly}
              inheritedFrom={parentTierName}
              inheritedValues={inheritedRevShare}
            />
          </Collapse>

          <Spacer height='s15' />

          <Collapse in={showTimingSection} timeout={300} unmountOnExit>
            <Box display='flex' flexDirection='column'>
              <FormFieldRow label='Timing' labelVariant='p18SemiBold' labelColor='secondary.dark' />
              <Spacer height='s15' />
              <Box display='flex' flexDirection='column' gap='25px'>
                <FormFieldRow label='Effective date'>
                  <FormDateTextField
                    fieldName='effectiveTimestamp'
                    disabled={isViewOnly}
                    sx={{ maxWidth: '120px' }}
                  />
                </FormFieldRow>

                <FormFieldRow label='Set end date?'>
                  <ToggleInput<RevShareFormValues>
                    fieldName='setEndDate'
                    items={['No', 'Yes']}
                    exclusive
                    buttonStyle={toggleButtonStyle}
                    readonly={isViewOnly || endDateRequired}
                    required={false}
                  />
                </FormFieldRow>

                <Collapse in={Boolean(setEndDate)} timeout={300} unmountOnExit>
                  <FormFieldRow label='Expiration date'>
                    <FormDateTextField
                      fieldName='expireTimestamp'
                      disabled={isViewOnly}
                      sx={{ maxWidth: '120px' }}
                    />
                  </FormFieldRow>
                </Collapse>
              </Box>
            </Box>
          </Collapse>
        </Box>
      </Box>

      {isViewOnly ? (
        <Row style={{ justifyContent: 'flex-start', marginTop: 'auto', paddingTop: '24px' }}>
          <Button variant='outlined' color='secondary' style={{ width: 120 }} onClick={onCancel}>
            Close
          </Button>
        </Row>
      ) : (
        <Row style={{ justifyContent: 'space-between', marginTop: 'auto', paddingTop: '24px' }}>
          <Button variant='outlined' color='secondary' style={{ width: 120 }} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='primary'
            style={{ width: 120 }}
            disabled={disableSave}
            onClick={handleSubmit}>
            Save
          </Button>
        </Row>
      )}
    </Box>
  );
};

export default RevShareEntryForm;
